import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, Send, Image, X, Video, Reply, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";
import useWebSocket from "../hooks/useWebSocket";
import MessageBubble from "./MessageBubble";
import ImageViewer from "./ImageViewer";
import VideoViewer from "./VideoViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format date separators
const formatDateSeparator = (date) => {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Reset time for comparison
  messageDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);
  
  if (messageDate.getTime() === today.getTime()) {
    return "Сегодня";
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return "Вчера";
  } else {
    return messageDate.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'long', 
      year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined 
    });
  }
};

// Helper to check if two dates are different days
const isDifferentDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.toDateString() !== d2.toDateString();
};

export default function GroupChat({ group, user, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [viewingVideo, setViewingVideo] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const { lastMessage } = useWebSocket(user);

  useEffect(() => {
    fetchMessages();
  }, [group.group_id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (lastMessage && lastMessage.type === "new_message") {
      const msg = lastMessage.data;
      if (msg.group_id === group.group_id) {
        setMessages(prev => {
          if (prev.find(m => m.message_id === msg.message_id)) return prev;
          return [...prev, { ...msg, _animateIn: true }];
        });
      }
    } else if (lastMessage && lastMessage.type === "reaction_update") {
      setMessages(prev => prev.map(m => 
        m.message_id === lastMessage.data.message_id 
          ? { ...m, reactions: lastMessage.data.reactions }
          : m
      ));
    } else if (lastMessage && lastMessage.type === "message_deleted") {
      setMessages(prev => prev.filter(m => m.message_id !== lastMessage.data.message_id));
    }
  }, [lastMessage, group.group_id]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/messages/group/${group.group_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки сообщений");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    const tempId = `temp_${Date.now()}`;
    const isVideoFile = selectedFile?.type?.startsWith("video/");
    
    const optimisticMessage = {
      message_id: tempId,
      sender_id: user?.user_id,
      sender_name: `${user?.first_name} ${user?.last_name}`,
      content: newMessage.trim() || (isVideoFile ? "🎬 Видео" : "📷 Фото"),
      image_url: selectedFile && !isVideoFile ? URL.createObjectURL(selectedFile) : null,
      media_url: selectedFile && isVideoFile ? URL.createObjectURL(selectedFile) : null,
      reactions: {},
      created_at: new Date().toISOString(),
      group_id: group.group_id,
      reply_to: replyTo ? { sender_name: replyTo.sender_name, content: replyTo.content } : null,
      _pending: true,
      _animateIn: true // Flag for animation
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage("");
    setReplyTo(null);
    const fileToUpload = selectedFile;
    setSelectedFile(null);

    try {
      const token = localStorage.getItem("token");
      let mediaUrl = null;

      if (fileToUpload) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", fileToUpload);
        
        const uploadResponse = await axios.post(`${API}/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          withCredentials: true
        });
        mediaUrl = uploadResponse.data.file_url;
        setUploading(false);
      }

      const response = await axios.post(`${API}/messages`, {
        content: optimisticMessage.content,
        image_url: !isVideoFile ? mediaUrl : null,
        media_url: isVideoFile ? mediaUrl : null,
        group_id: group.group_id,
        reply_to_id: replyTo?.message_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      setMessages(prev => prev.map(m => 
        m.message_id === tempId ? response.data : m
      ));
    } catch (error) {
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      toast.error("Ошибка отправки сообщения");
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("Файл слишком большой (макс. 50MB)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleReaction = async (messageId, emoji) => {
    setMessages(prev => prev.map(m => {
      if (m.message_id !== messageId) return m;
      
      const reactions = { ...m.reactions };
      if (!reactions[emoji]) {
        reactions[emoji] = [];
      }
      
      const userId = user?.user_id;
      if (reactions[emoji].includes(userId)) {
        reactions[emoji] = reactions[emoji].filter(id => id !== userId);
        if (reactions[emoji].length === 0) {
          delete reactions[emoji];
        }
      } else {
        reactions[emoji] = [...reactions[emoji], userId];
      }
      
      return { ...m, reactions };
    }));

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/messages/reaction`, {
        message_id: messageId,
        emoji: emoji
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      fetchMessages();
      toast.error("Ошибка");
    }
  };

  const handleDelete = async (messageId) => {
    setMessages(prev => prev.filter(m => m.message_id !== messageId));
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      toast.error("Ошибка удаления");
      fetchMessages();
    }
  };

  const handleReply = (message) => {
    setReplyTo(message);
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-white shrink-0">
        <Button
          onClick={onBack}
          variant="ghost"
          size="icon"
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 flex flex-col items-center justify-center">
          <h2 className="font-heading font-semibold text-center">{group.name}</h2>
          <p className="text-xs text-apple-text-secondary text-center">
            {group.member_count} участников
          </p>
        </div>
        <div className="w-10"></div> {/* Spacer for centering */}
      </div>

      {/* Messages */}
      <div 
        className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50" 
        style={{ paddingBottom: (replyTo || selectedFile) ? '180px' : '100px' }}
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Users className="w-8 h-8 text-apple-text-tertiary" />
            </div>
            <p className="text-apple-text-secondary">Нет сообщений</p>
            <p className="text-sm text-apple-text-tertiary">Начните общение</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              // Check if we need to show date separator
              const showDateSeparator = index === 0 || isDifferentDay(messages[index - 1].created_at, message.created_at);
              
              return (
                <React.Fragment key={message.message_id}>
                  {showDateSeparator && (
                    <div className="flex justify-center my-4">
                      <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm border border-gray-200">
                        <span className="text-xs text-gray-600 font-medium">
                          {formatDateSeparator(message.created_at)}
                        </span>
                      </div>
                    </div>
                  )}
                  <MessageBubble
                    message={message}
                    currentUserId={user?.user_id}
                    isMasterUser={user?.is_master}
                    onReaction={handleReaction}
                    onDelete={handleDelete}
                    onReply={handleReply}
                    onImageClick={setViewingImage}
                    onVideoClick={setViewingVideo}
                  />
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Reply Preview - Fixed above input with message bubble style */}
      {replyTo && (
        <div className="fixed bottom-20 left-0 right-0 px-4 py-3 bg-gray-50 z-10 border-t border-gray-200">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-3 shadow-md border border-gray-200 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-golf-green font-semibold mb-1">Ответ на {replyTo.sender_name}</p>
                <p className="text-sm text-gray-700 truncate">{replyTo.content}</p>
              </div>
              <Button
                onClick={() => setReplyTo(null)}
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full shrink-0 ml-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview - Fixed above input with message bubble style */}
      {selectedFile && (
        <div className="fixed bottom-20 left-0 right-0 px-4 py-3 bg-gray-50 z-10 border-t border-gray-200">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-3 shadow-md border border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {selectedFile.type.startsWith("video/") ? (
                  <Video className="w-5 h-5 text-blue-500 shrink-0" />
                ) : (
                  <Image className="w-5 h-5 text-blue-500 shrink-0" />
                )}
                <span className="text-sm text-gray-700 truncate">{selectedFile.name}</span>
              </div>
              <Button
                onClick={() => setSelectedFile(null)}
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full shrink-0 ml-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Input Area - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 z-10">
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="ghost"
            size="icon"
            className="h-11 w-11 rounded-xl"
            disabled={uploading}
          >
            <Image className="w-5 h-5" />
          </Button>
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Сообщение..."
            className="flex-1 h-11 bg-apple-gray border-transparent rounded-xl"
            disabled={uploading}
            autoComplete="off"
          />
          <Button
            onClick={handleSendMessage}
            className="h-11 w-11 rounded-xl bg-golf-green hover:bg-golf-green-hover"
            disabled={uploading || (!newMessage.trim() && !selectedFile)}
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Image Viewer */}
      {viewingImage && (
        <ImageViewer imageUrl={viewingImage} onClose={() => setViewingImage(null)} />
      )}

      {/* Video Viewer */}
      {viewingVideo && (
        <VideoViewer videoUrl={viewingVideo} onClose={() => setViewingVideo(null)} />
      )}
    </div>
  );
}
